/**
 * A fully type-safe event emitter.
 *
 * Declare an event map (`event name → payload type`) and every `on`/`emit`/
 * `waitFor` is checked and inferred against it. Includes a wildcard listener and
 * a promise-based {@link Emitter.waitFor} with `AbortSignal` support.
 */

/** A map of event name → payload type. */
export type EventMap = Record<string, unknown>

/** A listener for a single event. */
export type Handler<T> = (payload: T) => void
/** A listener invoked for every event, receiving its name and payload. */
export type WildcardHandler<E extends EventMap> = <K extends keyof E>(event: K, payload: E[K]) => void

/** Returned by `on`/`once`/`onAny` to remove that listener. */
export type Unsubscribe = () => void

/** Makes the payload argument optional when the event's type permits `void`. */
type EmitArgs<T> = [T] extends [void] ? [payload?: T] : [payload: T]
/** Options for {@link Emitter.waitFor}. */
export interface WaitForOptions<T> {
  /** Reject the wait when this signal aborts. */
  signal?: AbortSignal
  /** Only resolve for a payload that passes this predicate. */
  filter?: (payload: T) => boolean
}
/** A typed event emitter. */
export interface Emitter<E extends EventMap> {
  /** Subscribe to `event`. Returns an unsubscribe function. */
  on<K extends keyof E>(event: K, handler: Handler<E[K]>): Unsubscribe
  /** Subscribe to the next `event` only. Returns an unsubscribe function. */
  once<K extends keyof E>(event: K, handler: Handler<E[K]>): Unsubscribe
  /** Remove a specific handler added with `on`. */
  off<K extends keyof E>(event: K, handler: Handler<E[K]>): void
  /** Emit `event` with its payload (payload optional for `void` events). */
  emit<K extends keyof E>(event: K, ...args: EmitArgs<E[K]>): void
  /** Subscribe to all events. Returns an unsubscribe function. */
  onAny(handler: WildcardHandler<E>): Unsubscribe
  /** Remove a wildcard handler. */
  offAny(handler: WildcardHandler<E>): void
  /** Resolve with the next `event` payload (optionally filtered); rejects on abort. */
  waitFor<K extends keyof E>(event: K, options?: WaitForOptions<E[K]>): Promise<E[K]>
  /** Remove all listeners for `event`, or all listeners if omitted. */
  clear<K extends keyof E>(event?: K): void
  /** Count listeners for `event`, or all listeners (incl. wildcard) if omitted. */
  listenerCount<K extends keyof E>(event?: K): number
}
function abortError(signal?: AbortSignal): unknown {
  return signal?.reason ?? new DOMException("This operation was aborted", "AbortError")
}
/**
 * Create a type-safe {@link Emitter}.
 *
 * @example
 * ```ts
 * const bus = createEmitter<{ login: { id: string }; logout: void }>();
 * const off = bus.on("login", (u) => console.log(u.id)); // u: { id: string }
 * bus.emit("login", { id: "42" });
 * bus.emit("logout");                                     // payload optional
 * const next = await bus.waitFor("login", { signal });
 * off();
 * ```
 */
export function createEmitter<E extends EventMap>(): Emitter<E> {
  const handlers = new Map<keyof E, Set<Handler<unknown>>>()
  const wildcard = new Set<WildcardHandler<E>>()
  const off: Emitter<E>["off"] = (event, handler) => {
    handlers.get(event)?.delete(handler as Handler<unknown>)
  }

  const on: Emitter<E>["on"] = (event, handler) => {
    let set = handlers.get(event)
    if (!set) {
      set = new Set()
      handlers.set(event, set)
    }
    set.add(handler as Handler<unknown>)
    return () => off(event, handler)
  }
  const once: Emitter<E>["once"] = (event, handler) => {
    const wrapped: Handler<E[typeof event]> = (payload) => {
      off(event, wrapped)
      handler(payload)
    }
    return on(event, wrapped)
  }

  const emit: Emitter<E>["emit"] = (event, ...args) => {
    const payload = args[0] as E[typeof event]
    const set = handlers.get(event)
    if (set) for (const h of [...set]) h(payload)
    if (wildcard.size) for (const h of [...wildcard]) h(event, payload)
  }
  const onAny: Emitter<E>["onAny"] = (handler) => {
    wildcard.add(handler)
    return () => wildcard.delete(handler)
  }

  const offAny: Emitter<E>["offAny"] = (handler) => {
    wildcard.delete(handler)
  }

  const waitFor: Emitter<E>["waitFor"] = (event, options = {}) =>
    new Promise((resolve, reject) => {
      const { signal, filter } = options
      if (signal?.aborted) return reject(abortError(signal))
      const cleanup = () => {
        unsubscribe()
        signal?.removeEventListener("abort", onAbort)
      }
      const onAbort = () => {
        cleanup()
        reject(abortError(signal))
      }
      const unsubscribe = on(event, (payload) => {
        if (filter && !filter(payload)) return
        cleanup()
        resolve(payload)
      })
      signal?.addEventListener("abort", onAbort, { once: true })
    })
  const clear: Emitter<E>["clear"] = (event) => {
    if (event === undefined) {
      handlers.clear()
      wildcard.clear()
    } else {
      handlers.delete(event)
    }
  }

  const listenerCount: Emitter<E>["listenerCount"] = (event) => {
    if (event === undefined) {
      let total = wildcard.size
      for (const set of handlers.values()) total += set.size
      return total
    }
    return handlers.get(event)?.size ?? 0
  }
  return { on, once, off, emit, onAny, offAny, waitFor, clear, listenerCount }
}
